import EssenceAnimatedButton from '../../../components/essence/button/animated-button';
import Header from '../../../shared/components/header/page';
import Content from '../../../shared/components/content/page';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useState, useEffect, useMemo } from 'react';
import getMemberType from '../../../secure/circle-details/member-type';
import { useCircleQueries } from '../../../shared/tanstack/circle/queries';
import { useExpenseMutations } from '../../../shared/tanstack/expenses/mutations';
import { useQuery, useMutation } from '@tanstack/react-query';
import ParticipantsList from '../components/participant-list/page';
import { InvalidCircleIdError } from '../../../shared/models/errors/invalid-circle-id-error';
import WarningMessage from '../components/warning-message/page';
import { ContextExpenseUser, useExpenseContext } from '../../../contexts/expense/expense-context';
import accountService from '../../../shared/services/account-service';
import EssenceAnimatedRadio from '../../../components/essence/radio/animated-radio';
import styles from './page.module.css';
import amountService from '../../../shared/services/amount.service';
const AddEditExpenseStep3 = () => {
	const { id: circleId } = useParams();
	const { circleQuery, participantsQuery } = useCircleQueries();
	if (!circleId) {
		throw new InvalidCircleIdError();
	}
	const { data: circle } = useQuery(circleQuery(circleId));
	const { data: circleParticipants } = useQuery(participantsQuery(circleId));
	const { expense, getFinalExpense } = useExpenseContext();
	const [participants, setParticipants] = useState<ContextExpenseUser[]>();

	const { addExpenseMutation, updateExpenseMutation } = useExpenseMutations(circleId);
	const { mutate: addExpense, isError: isAddExpenseError } = useMutation(addExpenseMutation);
	const { mutate: updateExpense, isError: isUpdateExpenseError } = useMutation(updateExpenseMutation);
	const [distributeEqually, setDistributeEqually] = useState(true);

	const navigate = useNavigate();
	let member: string | undefined;
	const account = accountService.getAccount();
	const loggedInUserId = account?.id;
	if (circle) {
		const memberType = getMemberType(circle.type);
		if (memberType) {
			member = memberType.pluralTitle;
		}
	}
	const creditAmount = useMemo(
		() => participants?.reduce((sum, participant) => sum + amountService.getAmount(participant.amount), 0) ?? 0,
		[participants],
	);

	useEffect(() => {
		if (isAddExpenseError || isUpdateExpenseError) {
			alert('Some unknown error occured');
		}
	}, [isAddExpenseError, isUpdateExpenseError]);

	useEffect(() => {
		if (circleParticipants && loggedInUserId) {
			if (expense?.debtors && expense.debtors.length === 0) {
				setParticipants(
					circleParticipants.map(participant => {
						return {
							...participant,
							amount: amountService.getAmount(expense.amount) / circleParticipants.length,
							isChecked: true,
							currency: (expense.currency?.length ?? 0) > 0 ? expense.currency : '₹',
						};
					}),
				);
			} else {
				const updatedParticipants = circleParticipants.map(participant => {
					const debtor = expense?.debtors?.find(debtor => debtor.id === participant.id);
					const isChecked = debtor ? true : false;
					const newAmount = debtor ? amountService.getAmount(debtor.amount) : 0;
					return {
						...participant,
						isChecked,
						amount: newAmount,
						currency: (expense?.currency?.length ?? 0) > 0 ? expense?.currency : '₹',
					};
				});
				if (!expense?.id || distributeEqually) {
					updatedParticipants.map(participant => {
						participant.amount = amountService.getAmount(expense?.amount) / circleParticipants.length;
						participant.isChecked = true;
					});
				}
				setParticipants(updatedParticipants);
			}
		}
	}, [
		circleParticipants,
		expense?.amount,
		expense?.debtors,
		expense?.currency,
		expense?.id,
		distributeEqually,
		loggedInUserId,
	]);

	const onSaveExpense = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const finalExpense = getFinalExpense();
		if (!finalExpense) {
			return;
		}

		const debtors = participants
			?.filter(participant => amountService.getAmount(participant.amount) > 0)
			.map(participant => ({
				id: participant.id,
				amount: amountService.getAmount(participant.amount),
				currency: (participant.currency?.length ?? 0) > 0 ? (participant.currency ?? '₹') : '₹',
				circleId: circleId,
			}));

		if (!expense?.id) {
			addExpense({
				...finalExpense,
				debtors: debtors,
			});
			navigate(-3);
		} else {
			updateExpense({
				...finalExpense,
				debtors: debtors,
			});
			navigate(-4);
		}
	};

	const form = {
		get isDisabled() {
			return amountService.getAmount(expense?.amount) - creditAmount !== 0 ? true : undefined;
		},
	};

	const onDistributeEqually = () => {
		setDistributeEqually(true);
		if (participants) {
			const updatedParticipants = participants.map(participant => {
				participant.amount = amountService.getAmount(expense?.amount) / participants.length;
				participant.isChecked = true;
				return participant;
			});
			setParticipants(updatedParticipants);
		}
	};

	const onDistributeManually = () => {
		setDistributeEqually(false);
	};

	return (
		<>
			{member && (
				<Header title={member} subtitle={`Select the list of ${member} who are a part of the expense.`} />
			)}
			<Content>
				<div className="flex flex-row gap-4">
					<EssenceAnimatedRadio
						defaultChecked={true}
						key={'distribute-equally'}
						text={'Equal Distribution'}
						name="distribution"
						value={'distribute-equally'}
						onChange={onDistributeEqually}
					/>
					<EssenceAnimatedRadio
						defaultChecked={false}
						key={'distribute-manually'}
						text={'Manual Distribution'}
						name="distribution"
						value={'distribute-manually'}
						onChange={onDistributeManually}
					/>
				</div>

				{distributeEqually &&
					participants?.map(participant => {
						return (
							<div key={participant.id} className="flex flex-row gap-4 caption">
								<div>{participant.name}</div>
								<div>
									{participant.currency} {participant.amount}
								</div>
							</div>
						);
					})}
				{!distributeEqually && (
					<ul className={styles['participants-list']}>
						{participants && (
							<ParticipantsList
								currency={(expense?.currency?.length ?? 0) > 0 ? (expense?.currency ?? '₹') : '₹'}
								totalAmount={amountService.getAmount(expense?.amount)}
								circleParticipants={participants}
								onChange={setParticipants}
								loggedInUserId={loggedInUserId ?? ''}
							/>
						)}
						{form.isDisabled && (
							<WarningMessage amount1={amountService.getAmount(expense?.amount)} amount2={creditAmount} />
						)}
					</ul>
				)}

				<EssenceAnimatedButton
					onClick={onSaveExpense}
					name="save"
					disabled={form.isDisabled}
					className="mt-4"
				/>
			</Content>
		</>
	);
};

export default AddEditExpenseStep3;
